import ClearIcon from '@mui/icons-material/Clear'
import { Autocomplete, TextField as MuiTextField, TextFieldProps } from '@mui/material'
import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import { AutocompleteBaseOption } from '../../types/common'

type Props<T> = {
  name: string
  label: string
  placeholder?: string
  shrink?: boolean
  options: T[]
  loading?: boolean
  limitTags?: number
  onInputChange: (e: React.SyntheticEvent<Element, Event>, value: string) => void
  onChange?: (value: T[]) => void
  getOptionLabel?: (option: T) => string
}

export default function FormikExternalMultiAutocomplete<T extends AutocompleteBaseOption = AutocompleteBaseOption>(props: Props<T>) {
  const texts = useTranslation('common').t
  const [field, meta, helpers] = useField<T[]>(props.name)

  return (
    <Autocomplete<T, true, false, false>
      multiple
      loading={props.loading}
      loadingText={texts('loading_placeholder')}
      noOptionsText={texts('no_options_placeholder')}
      value={field.value}
      options={props.options}
      filterSelectedOptions
      filterOptions={(options) => options}
      isOptionEqualToValue={(option: T, value: T) => option.value === value.value}
      getOptionLabel={(option: T | string) => {
        return typeof option === 'string'
          ? option
          : (props.getOptionLabel?.(option) ?? option.label)
      }}
      onChange={(_, value: T[]) => {
        helpers.setValue(value)
        props.onChange?.(value)
      }}
      // any as props type because type does not include key field (which exists in props)
      renderOption={(props: any, option) => (
        <li {...props} key={option.value}>
          {/* label from key should use getOptionLabel result*/}
          {/* fallthrough to option.label just in case */}
          {props.key ?? option.label} 
        </li>
      )}
      onInputChange={props.onInputChange}
      clearIcon={<ClearIcon />}
      limitTags={props.limitTags}
      renderInput={(params: TextFieldProps) => (
        <MuiTextField
          {...params}
          name={props.name}
          onBlur={field.onBlur}
          label={props.label}
          placeholder={field.value.length > 0 ? undefined : props.placeholder}
          error={meta.touched && !!meta.error}
          helperText={meta.touched && !!meta.error ? meta.error : ' '}
          InputLabelProps={{
            ...params.InputLabelProps,
            ...(props.shrink != null ? { shrink: props.shrink } : {})
          }}
        />
      )}
    />
  )
}